import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import Media from 'utils/media';
import throttle from 'lodash.throttle';
import clsx from 'clsx';
import ChevronDownIcon from 'utils/icon/icon-chevron-down.svg';
import ProductGalleryModal from './product-gallery-modal';

const ProductGalleryDesktop = ({
  currentProduct: { media, lazyLoadDesktop, enableMute },
}) => {

  const [activeIndex, setActiveIndex] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const isMounted = useRef(false);
  const mediaRefs = useRef([]);
  const container = useRef(null);  

  const updateHeight = () => {
    if (mediaRefs.current?.[4]) {
      let maxHeight = mediaRefs.current[4].parentElement.offsetHeight;
      if (!expanded) {
        maxHeight =
          mediaRefs.current[4].offsetTop + mediaRefs.current[4].offsetHeight;
      }
      container.current.style.maxHeight = `${maxHeight}px`;
    }
  };

  useEffect(() => {
    updateHeight();
  }, []);

  useEffect(() => {
    updateHeight();
    if (isMounted.current) {
      if (!expanded) {
        mediaRefs.current[4].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    } else {
      isMounted.current = true;
    }
    const handleResize = throttle(() => updateHeight(), 400);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [expanded]);

  const handleClick = (index) => {
    setActiveIndex(index);
    const body = document.querySelector('body');
    if (index !== null) {
      body.classList.add('overflow-hidden');
    } else {
      body.classList.remove('overflow-hidden');
    }
  };


  return (
    <div className="relative">
      <div
        className={clsx('ease-linear duration-300 overflow-hidden', {
          'max-h-full': media.length < 6,
        })}
        ref={container}
      >
        <div className="grid grid-cols-2 lc:gap-4 relative">
          {activeIndex || activeIndex === 0 ? (
            <ProductGalleryModal
              media={media}
              initialIndex={activeIndex}
              lazyLoadDesktop={lazyLoadDesktop}
              onClose={() => handleClick(null)}
              enableMute ={enableMute?.value}
            />
          ) : null}
          {media.map((item, index) => (
            <div
              key={item.previewImage.id}
              onClick={() => handleClick(index)}
              ref={(el) => {
                mediaRefs.current[index] = el;
              }}
              className={`relative pb-[136.5%] cursor-zoom-in
            ${index === 2 ? 'col-span-2' : ''}`}
            >
              <Media
                {...item}
                onClick={() => handleClick(index)}
                className="absolute inset-0 object-cover h-full w-full"
                lazyLoad={index > 2 && lazyLoadDesktop}
                forceLazyLoad={index > 2 && lazyLoadDesktop}
                srcWidths={{
                  sm: 640,
                  md: 768,
                  lg: 1024,
                  xl: 1280,
                  '2xl': 1536,
                }}
                activeIndex={activeIndex}
                autoPlay={true}
                enableMute ={enableMute?.value}
              />
            </div>
          ))}
        </div>
      </div>
      {media?.length > 5 && (
        <div
          onClick={() => setExpanded((value) => !value)}
          className={clsx('flex items-end justify-center cursor-pointer', {
            'h-[127px] absolute bottom-0 left-0 w-full bg-gradient-to-t from-white pb-16':
              !expanded,
            'py-24': expanded,
          })}
        >
          <button
            className={clsx(
              'ml:cta-primary flex  items-center justify-center gap-8 lc:heading-3-alt lc:!font-normal',
              {
                'flex-col': expanded,
                'flex-col-reverse': !expanded,
              },
            )}
          >
            <ChevronDownIcon
              className={clsx('ml:hidden w-24 h-auto', {
                'rotate-180': expanded,
              })}
            />
            {window.TMW.Strings.general[expanded ? 'showLess' : 'showMore']}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductGalleryDesktop;
